import { useEffect, useState } from 'react';
import { ensureLocaleMessages, hasLocaleMessages, toAppLocale } from './locale-messages';

/**
 * For UI that formats text in a locale other than the page's (e.g. a Finnish saved search listed
 * on the English hub). The browser only preloads the page's own locale, so this fetches any other
 * locales listed and re-renders once they arrive. Until then, catalogue lookups for those locales
 * fall back to the English defaultMessage.
 *
 * Returns a counter that goes up each time a locale finishes loading: add it to the deps of any
 * useMemo that formats in those locales, so the memo recomputes with the real messages.
 */
export function useLocaleMessagesFor(locales: readonly (string | null | undefined)[]): number {
  const key = [...new Set(locales.map(toAppLocale))].sort().join(',');
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    const missing = key.split(',').filter((loc) => loc && !hasLocaleMessages(loc));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map((loc) => ensureLocaleMessages(loc))).then(
      () => {
        if (!cancelled) setLoadedCount((n) => n + 1);
      },
      (err) => console.error('[i18n] failed to load locale messages', err),
    );
    return () => {
      cancelled = true;
    };
  }, [key]);

  return loadedCount;
}
